import { useQuery } from '@tanstack/react-query';
import pluralize from 'pluralize';
import * as React from 'react';
import {
  fetchOutstandingFeesBreakdown,
  InsightsQueryKeys,
  OutstandingFeesBreakdown,
  OutstandingFeesType,
} from 'services/clients/insights';
import { centsToDollars } from 'utils/container-transforms';
import { Loader } from 'components/Loaders/loader';
import {
  getParamsForView,
  linkFromParams,
} from 'pages/Shipments/components/HorizontalGraph/formatters';
import { HorizontalGraph, LabelFormatter, Row, Tabs } from '../../components/HorizontalGraph';
import { SHIPMENTS_DASHBOARD_QUERY_PARAMS, useDashboardParams } from '../params';
import { Legend } from './Legend';

interface Props {
  type: OutstandingFeesType;
}

const createOptionMap: () => Record<OutstandingFeesType, OutstandingFeesBreakdown[]> = () => ({
  [OutstandingFeesType.Demurrage]: [
    OutstandingFeesBreakdown.ShippingLine,
    OutstandingFeesBreakdown.Port,
    OutstandingFeesBreakdown.Terminal,
    OutstandingFeesBreakdown.Container,
  ],
  [OutstandingFeesType.PerDiem]: [
    OutstandingFeesBreakdown.ShippingLine,
    OutstandingFeesBreakdown.Port,
    OutstandingFeesBreakdown.Container,
    OutstandingFeesBreakdown.OceanBOL,
  ],
});

const PARAM_MAP: Record<OutstandingFeesType, keyof typeof SHIPMENTS_DASHBOARD_QUERY_PARAMS> = {
  [OutstandingFeesType.Demurrage]: 'demurrageBreakdown',
  [OutstandingFeesType.PerDiem]: 'perDiemBreakdown',
};

export const OutstandingFeesGraph: React.FC<Props> = (props) => {
  const { type } = props;

  const { searchParams, setSearchParams } = useDashboardParams();

  const optionMap = React.useMemo(() => createOptionMap(), []);

  const [view, setView] = React.useMemo(() => {
    const param = PARAM_MAP[type];
    const value = searchParams[param] as OutstandingFeesBreakdown;
    const setValue = (v: OutstandingFeesBreakdown) => {
      setSearchParams({ [param]: v });
    };
    return [value, setValue];
  }, [type, searchParams, setSearchParams]);

  const dataQuery = useQuery({
    queryKey: [InsightsQueryKeys.OutstandingFeesBreakdown, type, view],
    queryFn: ({ signal }) => fetchOutstandingFeesBreakdown({ type, breakdown: view }, signal),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  const labelFormatter: LabelFormatter = React.useCallback(
    (label, key, v) => {
      if (type === OutstandingFeesType.Demurrage) {
        return label;
      }

      const params = {
        hasPerDiemFees: type === OutstandingFeesType.PerDiem ? true : undefined,
        ...getParamsForView(label, key, v),
      };
      return linkFromParams(label, params);
    },
    [type]
  );

  const rows: Row[] = React.useMemo(() => {
    if (!dataQuery.data) return [];

    const { data } = dataQuery;

    return data.map((r) => {
      const row: Row = {
        key: r.key,
        label: r.label,
        data: [
          {
            redAmount: r.amount,
            blueAmount: 0,
            detail: `${
              view !== OutstandingFeesBreakdown.Container
                ? `${r.numberOfContainers} ${pluralize('container', r.numberOfContainers)} · `
                : ''
            }${centsToDollars(r.amount, false)} `,
            variant: 'current',
          },
        ],
      };
      return row;
    });
  }, [dataQuery, view]);

  let inner = null;
  if (dataQuery.isLoading) {
    inner = <Loader />;
  } else if (dataQuery.isError) {
    inner = <div className="text-center text-gray-500 py-4">Error loading data</div>;
  } else {
    inner = (
      <HorizontalGraph
        rows={rows}
        loading={dataQuery.isLoading}
        error={dataQuery.isError}
        scrollable
        view={view}
        labelFormatter={labelFormatter}
        className="max-h-[600px]"
      />
    );
  }

  return (
    <div className="relative min-h-0 space-y-4 flex flex-col">
      <Tabs
        value={view}
        options={optionMap[type]}
        onChange={(value) => setView(value)}
        disabled={dataQuery.isLoading || dataQuery.isError}
      />
      <Legend type={type} />
      {inner}
    </div>
  );
};
OutstandingFeesGraph.displayName = 'Shipments.Dashboard.OutstandingFees.Graph';
